import type { ReactNode } from 'react';
import { Text } from '@repletoryreach/sdk-ui';
import { TextInput, SegmentedControl } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../i18n/I18nProvider';
import { DATE_RANGE_KEYS, type DateRangeKey } from './dateRange';
import './billing.css';

/**
 * The one control row every billing list page opens with: a search box that
 * grows, the date-range segmented control, then whatever the page puts on the
 * right (a Filters drawer, an export, a limit selector).
 *
 * It sits above the table rather than inside `TableShell`'s own toolbar because
 * the range and the filters narrow what the table counts, so they have to be
 * readable before the operator looks at the numbers under them.
 */
export function BillingToolbar({
  search, onSearch, searchPlaceholder,
  range, onRangeChange,
  children
}: {
  search: string;
  onSearch: (value: string) => void;
  searchPlaceholder: string;
  /** Omit both range props on a page whose rows carry no timestamp. */
  range?: DateRangeKey;
  onRangeChange?: (next: DateRangeKey) => void;
  /** Right-hand controls: Filters, export, anything page-specific. */
  children?: ReactNode;
}) {
  const t = useT();
  const rangeLabels: Record<DateRangeKey, string> = {
    all: t('billing.range.all'),
    today: t('billing.range.today'),
    '7d': t('billing.range.last7'),
    '30d': t('billing.range.last30')
  };

  return (
    <div className="rr-billing-toolbar">
      <TextInput
        size="sm"
        className="rr-billing-toolbar__search"
        value={search}
        onChange={(e) => onSearch(e.currentTarget.value)}
        placeholder={searchPlaceholder}
        aria-label={searchPlaceholder}
        leftSection={(
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
            <circle cx="6" cy="6" r="4" />
            <line x1="9" y1="9" x2="12.5" y2="12.5" strokeLinecap="round" />
          </svg>
        )}
      />

      {range !== undefined && onRangeChange && (
        <div className="rr-billing-toolbar__range">
          <Text role="bodySmall" fw={600} c="var(--rr-color-text-muted)">{t('billing.range.label')}</Text>
          <SegmentedControl
            size="xs"
            value={range}
            onChange={(v) => onRangeChange(v as DateRangeKey)}
            data={DATE_RANGE_KEYS.map((k) => ({ value: k, label: rangeLabels[k] }))}
          />
        </div>
      )}

      <div className="rr-billing-toolbar__end">{children}</div>
    </div>
  );
}
